import { useState, useEffect, useRef, useCallback } from 'react'
import SpinnerBlock from '@components/spinner/SpinnerBlock'
import Icon from '@components/shared/Icon'
import coverPhoto from '@utils/links/coverPhoto'
import plural from '@utils/grammar/plural'
import useT from '@hooks/useTranslation'
import { FUELS, STATUSES } from 'lib/carfax/constants'
import MessageHelper from '@utils/messageHelper'

import styles from '../styles/Carfax.module.css'

const API = '/api/carfax/:vin'
const VIN_PARTIAL_REGEXP = /^[A-HJ-NPR-Z0-9]*$/
const CARFAX_LINK = 'https://www.carfax.eu/pl/partner-samochody'

export default function Carfax ({ carfaxVinData, offer }) {
    const [quoteRequested, setQuoteRequested] = useState(false)
    const [data, setData] = useState(carfaxVinData)
    const [loading, setLoading] = useState(false)
    const [vin, setVin] = useState(offer.vin || '')

    const mounted = useRef(false)

    const vinIsValid = vin.length === 17
    const dataFromOffer = Boolean(offer.vin && carfaxVinData)

    const requestQuote = useCallback(() => setQuoteRequested(true), [])

    useEffect(() => {
        mounted.current = true
        return () => { mounted.current = false }
    }, [])

    useEffect(() => {
        if (vinIsValid && quoteRequested) {
            setLoading(true)
            fetch(API.replace(':vin', vin))
                .then(res => res.json())
                .then(res => {
                    if (!mounted.current) return
                    if (res.success) {
                        setData(res.data)

                    } else {
                        MessageHelper.summary(res)
                    }

                    setLoading(false)
                })
                .catch(err => null)
        }
    }, [vinIsValid, quoteRequested])

    return (
        <div className={styles.carfax}>
            <h2>{useT('Historia pojazdu')}</h2>
            { loading && <SpinnerBlock height={100} /> }
            { vinIsValid
                ? <CarfaxData
                    data={data}
                    dataFromOffer={dataFromOffer}
                    loading={loading}
                    offer={offer}
                    requestQuote={requestQuote} />
                : <VinInput vin={vin} callback={setVin} /> }
            <CarfaxBranding offer={offer} vin={vin} />
        </div>
    )
}

function CarfaxData ({ data, dataFromOffer, loading, offer, requestQuote }) {
    const text = {
        historii: useT('z historii'),
        identyfikacja: useT('Identyfikacja pojazdu na podstawie danych z ogłoszenia'),
        marka: useT('Marka'),
        model: useT('Model'),
        oc: useT('Status OC'),
        paliwo: useT('paliwo'),
        raport: useT('Raport dla tego pojazdu zawiera'),
        rejestracja: useT('Stan rejestracji'),
        rocznik: useT('rocznik'),
        sprawdz: useT('Sprawdź'),
        rejestracjaOC: useT('Sprawdź stan rejestracji i OC'),
        zdarzenie: [useT('zdarzenie'),useT('zdarzenia'),useT('zdarzeń')],
    }

    const make = offer.make || data?.make
    const model = offer.model || data?.model
    const year = offer.production_year || data?.vehicleYear
    const fuel = offer.fuel || data?.fuel
    const fuelPL = FUELS[fuel] || fuel

    const renderData = Boolean(data)
    const renderQuoteButton = !renderData && !loading

    const { insurance, registration, reports } = data || {}

    const renderReport = Boolean(reports)

    return (
        <div>
            { dataFromOffer &&
                <p><b>{text.identyfikacja}</b></p> }

            <p className={styles['spec-row']}>
                { make && <span><span>{text.marka}:</span><span><b>{make}</b></span></span> }
                { model && <span><span>{text.model}:</span><span><b>{model}</b></span></span> }
                { year && <span><span>{text.rocznik}:</span><span><b>{year}</b></span></span> }
                { fuel && <span><span>{text.paliwo}:</span><span><b>{fuelPL}</b></span></span> }
            </p>

            {
                renderQuoteButton
                    ? <>
                        <p><b className={styles.teal}>{text.rejestracjaOC}:</b></p>
                        <button className={styles.check} onClick={requestQuote}><b>{text.sprawdz}</b></button>
                        <br/><br/>
                      </>

                    : <>
                        <p className={styles['status-row']}>
                            { registration && <span><span>{text.rejestracja}:</span><CarfaxIcon icon={registration} /></span> }
                            { insurance && <span><span>{text.oc}:</span><CarfaxIcon icon={insurance} /></span> }
                        </p>

                        { renderReport && <p>
                            <b>{text.raport} {reports} {plural(reports,text.zdarzenie)} {text.historii}.</b>
                          </p>
                        }
                      </>
            }
        </div>
    )
}

function CarfaxIcon ({ icon }) {
    const details = STATUSES[icon]
    if (details) {
        const { color, icon, t } = details
        return (
            <span>
                <span><b>{useT(t)}</b></span>
                <span className={styles.icon} data-color={color}><Icon>{icon}</Icon></span>
            </span>
        )

    } else {
        return null
    }
}

function VinInput ({ vin, callback }) {
    const text = {
        niePodal: useT('Sprzedający nie podał numeru VIN'),
        skorzystaj: useT('Skorzystaj z danych kontaktowych wskazanych w ogłoszeniu i zapytaj o VIN, a następnie wprowadź go w pole poniżej i dowiedz się więcej o historii tego pojazdu'),
        wprowadz: useT('Wprowadź numer VIN'),
    }

    const onChange = ({target:{value=''}}) => {
        const vin = value.toUpperCase()
        if (VIN_PARTIAL_REGEXP.test(vin)) {
            callback(vin)
        }
    }

    return <>
        <p>{text.niePodal}.</p>
        <p>{text.skorzystaj}.</p>
        <h3>{text.wprowadz}</h3>
        <input name="vin" className={styles.input} onChange={onChange} value={vin} />
        <br/><br/>
    </>
}

function CarfaxBranding ({ offer, vin }) {
    const linkProps = {target:'_blank',rel:'noopener noreferrer nofollow',style:{textDecoration:'underline'}}
    const offerLink = carfaxLink(offer, vin)
    const offerLinkText = useT('Pobierz raport CARFAX za opłatą')
    const carfaxLinkText = useT('Przykładowy raport carfax')

    return <>
        <a {...linkProps} href={offerLink}>{offerLinkText}</a>
        <br/><br/>
        <a {...linkProps} href="https://www.carfax.eu/static/images/sample-reports/pl/sample-report-desktop-2x.jpg">{carfaxLinkText}</a>
        <br/><br/>
        <img src="/assets/icons/carfax.jpg" alt="Carfax" style={{maxWidth:120}} />
        <br/><br/>
    </>
}

function carfaxLink ({ _shortid, make, model, tracking: { pid = ''} = {}}, vin) {
    if (!_shortid || !make || !model || !vin) return CARFAX_LINK
    return `${CARFAX_LINK}?vin=${encodeURIComponent(vin || '')}&make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}&adid=${encodeURIComponent(_shortid)}&pic=${coverPhoto(pid)}?w=3x#utm_medium=website&utm_source=samochody.pl`
}